/**
 * Exercise pattern DSL (B1) + auto-transposition (B2).
 *
 * An exercise is a parameterized pattern — semitone offsets from a movable
 * root — rather than a fixed recording, so a single definition serves every
 * voice. `renderPattern` turns a pattern + root + tempo into a concrete
 * timeline the player can synthesize and score against.
 */
/** Min/max semitone offset the pattern reaches across all ladder repeats. */
export function patternSpan(p) {
    const offsets = p.steps.map((s) => s.offset).filter((o) => o !== null);
    if (offsets.length === 0)
        return { min: 0, max: 0 };
    const min = Math.min(...offsets);
    const max = Math.max(...offsets) + Math.max(0, p.ladderRepeats - 1) * p.ladderStep;
    return { min, max };
}
/**
 * Auto-transposition (B2): choose a root so the exercise sits comfortably
 * inside the student's detected range, leaving `marginSemis` of headroom at
 * both extremes when possible. Returns the chosen root midi.
 */
export function autoTransposeRoot(pattern, rangeLowMidi, rangeHighMidi, marginSemis = 2) {
    const span = patternSpan(pattern);
    const usableLow = rangeLowMidi + marginSemis;
    const usableHigh = rangeHighMidi - marginSemis;
    // Ideal root centers the pattern in the usable range.
    const patternCenter = (span.min + span.max) / 2;
    let root = Math.round((usableLow + usableHigh) / 2 - patternCenter);
    // Clamp so extremes fit; if the range is too narrow for the pattern,
    // prioritize keeping the top inside the full range (strain risk is at the top).
    if (root + span.max > usableHigh)
        root = usableHigh - span.max;
    if (root + span.min < usableLow)
        root = usableLow - span.min;
    if (root + span.max > rangeHighMidi)
        root = rangeHighMidi - span.max;
    return Math.round(root);
}
/** Render a pattern to an absolute note timeline. */
export function renderPattern(pattern, rootMidi, tempoBpm) {
    const secPerBeat = 60 / tempoBpm;
    const notes = [];
    let t = 0;
    let lowest = Infinity;
    let highest = -Infinity;
    for (let rep = 0; rep < Math.max(1, pattern.ladderRepeats); rep++) {
        const repRoot = rootMidi + rep * pattern.ladderStep;
        for (let i = 0; i < pattern.steps.length; i++) {
            const step = pattern.steps[i];
            const durSec = step.beats * secPerBeat;
            const midi = step.offset === null ? null : repRoot + step.offset;
            let glideToMidi = null;
            if (step.glide) {
                const next = pattern.steps[i + 1];
                if (next && next.offset !== null)
                    glideToMidi = repRoot + next.offset;
            }
            if (midi !== null) {
                lowest = Math.min(lowest, midi, glideToMidi ?? midi);
                highest = Math.max(highest, midi, glideToMidi ?? midi);
            }
            notes.push({ midi, startSec: t, durSec, glideToMidi, syllable: step.syllable });
            t += durSec;
        }
    }
    return {
        notes,
        totalSec: t,
        lowestMidi: lowest === Infinity ? 0 : lowest,
        highestMidi: highest === -Infinity ? 0 : highest,
    };
}
/** Target midi (fractional during glides) at time t, or null in rests/gaps. */
export function targetMidiAt(rendered, tSec) {
    for (const n of rendered.notes) {
        if (tSec >= n.startSec && tSec < n.startSec + n.durSec) {
            if (n.midi === null)
                return null;
            if (n.glideToMidi !== null) {
                const frac = (tSec - n.startSec) / n.durSec;
                return n.midi + (n.glideToMidi - n.midi) * frac;
            }
            return n.midi;
        }
    }
    return null;
}
/**
 * Ear-training rendering: each ladder repeat becomes call (reference plays,
 * hidden from the student's canvas) followed by an equal-length response
 * window where the student sings the phrase back from memory. `playback` and
 * `target` share one timeline, so the standard player clock, recorder and
 * scoring pipeline work unchanged — scoring only counts response windows
 * because only those carry target notes.
 */
export function renderCallResponse(pattern, rootMidi, tempoBpm) {
    const secPerBeat = 60 / tempoBpm;
    const playbackNotes = [];
    const targetNotes = [];
    const segments = [];
    let t = 0;
    let lowest = Infinity;
    let highest = -Infinity;
    for (let rep = 0; rep < Math.max(1, pattern.ladderRepeats); rep++) {
        const repRoot = rootMidi + rep * pattern.ladderStep;
        // render one phrase at t=0, then place it at the call and response offsets
        const phrase = [];
        let pt = 0;
        for (let i = 0; i < pattern.steps.length; i++) {
            const step = pattern.steps[i];
            const durSec = step.beats * secPerBeat;
            const midi = step.offset === null ? null : repRoot + step.offset;
            let glideToMidi = null;
            if (step.glide) {
                const next = pattern.steps[i + 1];
                if (next && next.offset !== null)
                    glideToMidi = repRoot + next.offset;
            }
            if (midi !== null) {
                lowest = Math.min(lowest, midi, glideToMidi ?? midi);
                highest = Math.max(highest, midi, glideToMidi ?? midi);
            }
            phrase.push({ midi, startSec: pt, durSec, glideToMidi, syllable: step.syllable });
            pt += durSec;
        }
        const phraseDur = pt;
        for (const n of phrase)
            playbackNotes.push({ ...n, startSec: t + n.startSec });
        for (const n of phrase)
            targetNotes.push({ ...n, startSec: t + phraseDur + n.startSec });
        segments.push({ kind: 'call', startSec: t, endSec: t + phraseDur, rep });
        segments.push({ kind: 'response', startSec: t + phraseDur, endSec: t + 2 * phraseDur, rep });
        t += 2 * phraseDur;
    }
    const bounds = {
        totalSec: t,
        lowestMidi: lowest === Infinity ? 0 : lowest,
        highestMidi: highest === -Infinity ? 0 : highest,
    };
    return {
        playback: { notes: playbackNotes, ...bounds },
        target: { notes: targetNotes, ...bounds },
        segments,
    };
}
/** Segment containing time t (call/response mode UI hint). */
export function segmentAt(segments, tSec) {
    return segments.find((s) => tSec >= s.startSec && tSec < s.endSec) ?? null;
}
// ---------------------------------------------------------------------------
// Starter catalog helpers (B1) — pattern builders used by the seed script.
// ---------------------------------------------------------------------------
function even(offsets, beats = 1, syllable) {
    return offsets.map((offset) => ({ offset, beats, syllable }));
}
/**
 * A breathing break: an unpitched rest closing every phrase, so each ladder
 * repeat starts on a fresh breath. Silent in the synth, a gap on the canvas,
 * ignored by scoring — but it keeps the drill physically honest.
 */
function breath(beats = 2) {
    return { offset: null, beats, syllable: 'breathe' };
}
export const PATTERN_LIBRARY = {
    fiveToneScale: {
        kind: 'scale',
        steps: [...even([0, 2, 4, 5, 7, 5, 4, 2, 0], 0.5, 'ah'), breath()],
        defaultTempoBpm: 100,
        ladderRepeats: 5,
        ladderStep: 1,
    },
    majorScaleOctave: {
        kind: 'scale',
        steps: [...even([0, 2, 4, 5, 7, 9, 11, 12, 11, 9, 7, 5, 4, 2, 0], 0.5, 'ee'), breath(2.5)],
        defaultTempoBpm: 110,
        ladderRepeats: 3,
        ladderStep: 1,
    },
    majorArpeggio: {
        kind: 'arpeggio',
        steps: [...even([0, 4, 7, 12, 7, 4, 0], 0.5, 'ya'), breath()],
        defaultTempoBpm: 96,
        ladderRepeats: 5,
        ladderStep: 1,
    },
    octaveSiren: {
        kind: 'siren',
        steps: [
            { offset: 0, beats: 2, glide: true, syllable: 'ng' },
            { offset: 12, beats: 2, glide: true, syllable: 'ng' },
            { offset: 0, beats: 2, syllable: 'ng' },
            breath(),
        ],
        defaultTempoBpm: 60,
        ladderRepeats: 3,
        ladderStep: 2,
    },
    thirdsIntervals: {
        kind: 'interval',
        steps: [...even([0, 4, 2, 5, 4, 7, 5, 9, 7, 4, 0], 0.5, 'noo'), breath()],
        defaultTempoBpm: 90,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    fifthSlide: {
        kind: 'siren',
        steps: [
            { offset: 0, beats: 1.5, glide: true, syllable: 'oo' },
            { offset: 7, beats: 1.5, glide: true, syllable: 'oo' },
            { offset: 0, beats: 1, syllable: 'oo' },
            breath(1.5),
        ],
        defaultTempoBpm: 72,
        ladderRepeats: 5,
        ladderStep: 1,
    },
    breathHiss: {
        kind: 'breath',
        steps: [
            { offset: null, beats: 4, syllable: 'inhale' },
            { offset: null, beats: 8, syllable: 'sss' },
            { offset: null, beats: 4, syllable: 'inhale' },
            { offset: null, beats: 12, syllable: 'sss' },
        ],
        defaultTempoBpm: 60,
        ladderRepeats: 1,
        ladderStep: 0,
    },
    staccatoAgility: {
        kind: 'arpeggio',
        steps: [...even([0, 4, 7, 12, 16, 12, 7, 4, 0], 0.25, 'ha'), breath(1.5)],
        defaultTempoBpm: 120,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    lipTrillSiren: {
        kind: 'siren',
        steps: [
            { offset: 0, beats: 2, glide: true, syllable: 'brr' },
            { offset: 7, beats: 2, glide: true, syllable: 'brr' },
            { offset: 0, beats: 2, syllable: 'brr' },
            breath(),
        ],
        defaultTempoBpm: 66,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    minorFiveTone: {
        kind: 'scale',
        steps: [...even([0, 2, 3, 5, 7, 5, 3, 2, 0], 0.5, 'noh'), breath()],
        defaultTempoBpm: 96,
        ladderRepeats: 5,
        ladderStep: 1,
    },
    nineToneScale: {
        kind: 'scale',
        steps: [...even([0, 2, 4, 5, 7, 9, 11, 12, 14, 12, 11, 9, 7, 5, 4, 2, 0], 0.5, 'ah'), breath(3)],
        defaultTempoBpm: 108,
        ladderRepeats: 3,
        ladderStep: 1,
    },
    octaveLeaps: {
        kind: 'interval',
        steps: [
            { offset: 0, beats: 1, syllable: 'wee' },
            { offset: 12, beats: 2, syllable: 'wee' },
            { offset: 0, beats: 1, syllable: 'wee' },
            { offset: 12, beats: 2, syllable: 'wee' },
            { offset: 0, beats: 2, syllable: 'wee' },
            breath(),
        ],
        defaultTempoBpm: 84,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    longTones: {
        kind: 'custom',
        steps: [
            { offset: 0, beats: 8, syllable: 'ah' },
            breath(4),
            { offset: 0, beats: 8, syllable: 'oo' },
            breath(4),
        ],
        defaultTempoBpm: 60,
        ladderRepeats: 3,
        ladderStep: 2,
    },
    // ── Ear training (call & response): short phrases sung back from memory ──
    echoThreeNote: {
        kind: 'interval',
        steps: [...even([0, 2, 4], 1, 'la'), breath(1)],
        defaultTempoBpm: 90,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    echoTriad: {
        kind: 'arpeggio',
        steps: [...even([0, 4, 7, 4, 0], 0.75, 'da'), breath(1.25)],
        defaultTempoBpm: 96,
        ladderRepeats: 4,
        ladderStep: 1,
    },
    echoIntervalLeaps: {
        kind: 'interval',
        steps: [
            { offset: 0, beats: 1, syllable: 'na' },
            { offset: 5, beats: 1, syllable: 'na' },
            { offset: 0, beats: 1, syllable: 'na' },
            breath(1),
        ],
        defaultTempoBpm: 84,
        ladderRepeats: 5,
        ladderStep: 1,
    },
};
//# sourceMappingURL=patterns.js.map